// CardMailThread — the Gmail thread behind an email card, on the card itself
// (slice 4 of the mail arc, 2026-09-05).
// -----------------------------------------------------------------------------
// Renders for a card that carries `captureThreadId` (every card mail capture
// creates) and only for staff the server would let read it (admin/owner, or
// `permissions.sendEmail`) — the same gate the callables enforce, mirrored
// here so a teacher without it sees nothing rather than a permission error.
//
// Collapsed by default: the header shows the close signal from `mailReply`
// ("Awaiting our reply" / "Replied by Grace · Sep 4") without spending a
// Gmail read; "Show thread" loads the messages. The reply box turns plain
// text into a plain reply and creates a BYTE-VERIFIED draft on the thread
// through the same module the connector uses; the draft opens in Gmail, and
// sending stays there. No send button exists on this surface.
//
// Always-visible controls, quieted with colour, never hover-only (keratoconus
// rule; touch has no hover).

import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { getCardMailThread, createCardMailDraft, plainTextToReplyHtml } from '../../../services/cardMailService';

const STUDIO_DOMAIN = '@coloryourvoice.com';

function canSeeMail(userProfile) {
	if (!userProfile) return false;
	return userProfile.role === 'admin' || userProfile.owner === true || userProfile.permissions?.sendEmail === true;
}

function shortDate(iso) {
	if (!iso) return '';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function who(address) {
	const a = String(address || '');
	if (a === 'grace@coloryourvoice.com') return 'Grace';
	if (a === 'anthony@coloryourvoice.com' || a === 'sing@coloryourvoice.com') return 'Anthony';
	return a.endsWith(STUDIO_DOMAIN) ? a.replace(STUDIO_DOMAIN, '@') : a;
}

function displayName(from) {
	const m = String(from || '').match(/^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/);
	return m ? m[1].trim() : String(from || '').trim();
}

export function ReplyBadge({ mailReply }) {
	if (!mailReply) return <span className="wt-mailbadge wt-mailbadge--unknown">Not checked yet</span>;
	if (mailReply.awaiting) {
		return (
			<span className="wt-mailbadge wt-mailbadge--awaiting">
				<i className="fa-sharp fa-solid fa-reply" />
				Awaiting our reply
				{mailReply.lastInboundAt ? ` · since ${shortDate(mailReply.lastInboundAt)}` : ''}
			</span>
		);
	}
	if (mailReply.lastOutboundBy) {
		return (
			<span className="wt-mailbadge wt-mailbadge--replied">
				<i className="fa-sharp fa-solid fa-check" />
				Replied by {who(mailReply.lastOutboundBy)}
				{mailReply.lastOutboundAt ? ` · ${shortDate(mailReply.lastOutboundAt)}` : ''}
			</span>
		);
	}
	return <span className="wt-mailbadge wt-mailbadge--unknown">Nothing to answer</span>;
}

export default function CardMailThread({ task }) {
	const { userProfile } = useAuth();
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);
	const [reply, setReply] = useState('');
	const [drafting, setDrafting] = useState(false);
	const [drafted, setDrafted] = useState(null);
	// Who the reply goes out as. The choices and the default come from the
	// server with the thread (owners: studio / Anthony / Grace; others: studio),
	// so the rule lives in one place. Null until the thread has loaded.
	const [sendAs, setSendAs] = useState(null);

	if (!task || typeof task.captureThreadId !== 'string' || !task.captureThreadId) return null;
	if (!canSeeMail(userProfile)) return null;

	const load = async () => {
		setOpen(true);
		if (data || loading) return;
		setLoading(true);
		setError(null);
		try {
			const res = await getCardMailThread(task.id);
			setData(res);
			if (res && res.defaultSender) setSendAs(res.defaultSender);
		} catch (e) {
			setError(e?.message || 'Could not load the thread.');
		} finally {
			setLoading(false);
		}
	};

	const canDraft = reply.trim().length > 0 && !drafting;
	const draft = async () => {
		// Guard the handler too — a disabled attribute is never the only gate.
		if (!canDraft) return;
		setDrafting(true);
		setError(null);
		try {
			const res = await createCardMailDraft({ taskId: task.id, html: plainTextToReplyHtml(reply), text: reply.trim(), sendAs: sendAs || undefined });
			setDrafted(res);
			setReply('');
		} catch (e) {
			setError(e?.message || 'Could not create the draft.');
		} finally {
			setDrafting(false);
		}
	};

	const messages = data?.thread?.messages || [];
	const mailReply = data?.mailReply ?? task.mailReply ?? null;
	const senders = data?.senders || [];
	const chosen = senders.find((s) => s.key === sendAs) || senders[0] || null;

	return (
		<div className="wt-mail">
			<div className="wt-mail__head">
				<span className="wt-mail__title">
					<i className="fa-sharp fa-solid fa-envelope" />
					Email thread
				</span>
				<ReplyBadge mailReply={mailReply} />
				{!open && (
					<button type="button" className="wt-mail__toggle" onClick={load}>
						Show thread
					</button>
				)}
				{open && (
					<button type="button" className="wt-mail__toggle" onClick={() => setOpen(false)}>
						Hide
					</button>
				)}
			</div>

			{open && (
				<div className="wt-mail__body">
					{loading && <div className="wt-mail__note">Loading thread…</div>}
					{error && <div className="wt-mail__error">{error}</div>}
					{data && !data.thread && <div className="wt-mail__note">No thread found for this card.</div>}
					{messages.map((m) => {
						const ours = (m.labels || []).includes('SENT') || /@coloryourvoice\.com/i.test(m.from || '');
						return (
							<div key={m.messageId} className={`wt-mail__msg${ours ? ' wt-mail__msg--ours' : ''}`}>
								<div className="wt-mail__meta">
									<span className="wt-mail__from">{ours ? who((m.from.match(/<([^>]+)>/) || [])[1] || m.from) : displayName(m.from)}</span>
									<span className="wt-mail__date">{m.date}</span>
								</div>
								<div className="wt-mail__text">{m.text}{m.truncated ? ' …' : ''}</div>
							</div>
						);
					})}
					{data?.gmailUrl && (
						<a className="wt-mail__link" href={data.gmailUrl} target="_blank" rel="noreferrer">
							Open in Gmail
						</a>
					)}

					<div className="wt-mail__reply">
						<textarea
							value={reply}
							placeholder="Reply to the family… (creates a draft in Gmail; nothing is sent from here)"
							onChange={(e) => setReply(e.target.value)}
							rows={4}
						/>
						<div className="wt-mail__replyrow">
							<button type="button" className="btn-cyv-teal" onClick={draft} disabled={!canDraft}>
								{drafting ? 'Creating draft…' : 'Create draft in Gmail'}
							</button>
							{senders.length > 1 ? (
								<label className="wt-mail__sendas">
									<span>Send as</span>
									<select value={sendAs || ''} onChange={(e) => setSendAs(e.target.value)} aria-label="Send as">
										{senders.map((s) => (
											<option key={s.key} value={s.key}>
												{s.label}
											</option>
										))}
									</select>
								</label>
							) : null}
							<span className="wt-mail__note">
								{chosen
									? `Lands in ${chosen.landsIn}. Review and send from Gmail.`
									: 'Sends as Color Your Voice (sing@). Review and send from Gmail.'}
							</span>
						</div>
						{drafted && (
							<div className="wt-mail__drafted">
								<i className="fa-sharp fa-solid fa-shield-check" />
								Draft created and byte-verified ({drafted.verified?.checks?.length || 0} checks).{' '}
								<a href={drafted.gmailUrl} target="_blank" rel="noreferrer">
									Open the draft
								</a>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
